if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewerWrap: '.product__media-gallery-viewer-wrap',
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.showMoreExpanded = false;
        this.showMoreButtonBound = false;
        this.showMoreAnimationTimer = null;
        this.showMoreTransitionHandler = null;
        this.showMoreAnimationFrame = null;
        this.showMoreScrollFrame = null;

        this.handleShowMoreClick = this.handleShowMoreClick.bind(this);
      }

      connectedCallback() {
        // this.init();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.stopShowMoreAnimation();
        this.showMoreResizeObserver?.disconnect();

        if (this.showMoreButton && this.showMoreButtonBound) {
          this.showMoreButton.removeEventListener('click', this.handleShowMoreClick);
          this.showMoreButtonBound = false;
        }

        this.showMoreResizeObserver = null;
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setupShowMore();
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }
      }

      setupShowMore() {
        this.enableShowMore = this.dataset.enableShowMore === 'true' && this.mediaLayout === 'grid-mix';
        this.showMoreLimit = parseInt(this.dataset.showMoreLimit, 10) || 6;
        this.showMoreLimit = Math.max(1, this.showMoreLimit);
        this.showMoreButton = this.querySelector('[data-media-show-more-button]');
        this.showMoreContainer = this.querySelector('[data-media-show-more-container]');
        this.showMoreLabel = this.querySelector('[data-media-show-more-label]');
        this.showMoreText = this.dataset.showMoreText || 'Show More';
        this.showLessText = this.dataset.showLessText || 'Show Less';

        if (!this.enableShowMore || !this.showMoreButton || !this.elements.viewerWrap) return;

        // Older versions of this feature animated the Swiper viewer directly.
        // Clear any stale inline height before Swiper is initialized so its
        // percentage-height wrapper and slides can return to their natural size.
        this.elements.viewer?.style.removeProperty('height');

        if (!this.showMoreButtonBound) {
          this.showMoreButton.addEventListener('click', this.handleShowMoreClick);
          this.showMoreButtonBound = true;
        }

        if (!this.showMoreResizeObserver && typeof ResizeObserver !== 'undefined') {
          this.showMoreResizeObserver = new ResizeObserver(() => {
            if (this.classList.contains('product__media-gallery--show-more-animating')) {
              return;
            }

            if (!this.isShowMoreDesktop()) {
              this.resetShowMore(this.getShowMoreItems());
              return;
            }

            if (!this.showMoreExpanded) {
              this.updateShowMoreState({ animate: false });
            }
          });
          this.showMoreResizeObserver.observe(this.elements.mediaList);
        }
      }

      isShowMoreDesktop() {
        return window.matchMedia('(min-width: 768px)').matches;
      }

      getShowMoreItems() {
        if (!this.elements?.mediaList) return [];

        return Array.from(this.elements.mediaList.children).filter(
          (item) => item.classList.contains('product__media-item') && !item.classList.contains('swiper-slide-duplicate')
        );
      }

      updateShowMoreState({ animate = false } = {}) {
        const items = this.getShowMoreItems();
        const isAvailable =
          this.enableShowMore &&
          this.showMoreButton &&
          this.elements?.viewerWrap &&
          this.isShowMoreDesktop() &&
          items.length > this.showMoreLimit;

        if (!isAvailable) {
          this.resetShowMore(items);
          return;
        }

        this.classList.add('product__media-gallery--show-more-available');

        if (this.showMoreExpanded) {
          this.showAllMedia(items, animate);
        } else {
          this.collapseMedia(items, animate);
        }
      }

      handleShowMoreClick(event) {
        event.preventDefault();

        if (this.showMoreExpanded) {
          this.collapseMedia(this.getShowMoreItems(), true);
        } else {
          this.showAllMedia(this.getShowMoreItems(), true);
        }
      }

      showAllMedia(items, animate = true) {
        if (!items.length || !this.elements?.viewerWrap || !this.elements?.viewer) return;

        const startHeight = Math.ceil(this.elements.viewerWrap.getBoundingClientRect().height);

        this.stopShowMoreAnimation();
        this.elements.viewerWrap.style.height = `${startHeight}px`;
        this.classList.remove(
          'product__media-gallery--show-more-collapsed',
          'product__media-gallery--show-more-collapsing'
        );
        this.classList.add('product__media-gallery--show-more-expanding');
        this.setCollapsedPairLayout(items, false);
        this.setCollapsedMediaAccessibility(items, false);

        const fullHeight = Math.ceil(this.elements.viewer.getBoundingClientRect().height);
        this.showMoreExpanded = true;
        this.updateShowMoreButton(true);

        this.animateShowMoreHeight(startHeight, fullHeight, animate, () => {
          this.elements.viewerWrap.style.removeProperty('height');
          this.classList.remove('product__media-gallery--show-more-expanding');
          this.classList.add('product__media-gallery--show-more-expanded');
        });
      }

      collapseMedia(items, animate = true) {
        if (!items.length || !this.elements?.viewerWrap || !this.elements?.viewer) return;

        const startHeight = Math.ceil(this.elements.viewerWrap.getBoundingClientRect().height);

        this.stopShowMoreAnimation();
        this.elements.viewerWrap.style.height = `${startHeight}px`;
        this.classList.remove(
          'product__media-gallery--show-more-expanded',
          'product__media-gallery--show-more-expanding',
          'product__media-gallery--show-more-collapsed'
        );
        this.classList.add('product__media-gallery--show-more-collapsing');
        this.setCollapsedPairLayout(items, true);
        this.setCollapsedMediaAccessibility(items, true);

        const fullHeight = Math.ceil(this.elements.viewer.getBoundingClientRect().height);
        const collapsedHeight = Math.min(this.getCollapsedShowMoreHeight(items), fullHeight);
        const shouldTrackCollapseScroll =
          animate &&
          !this.prefersReducedMotion() &&
          Math.abs(startHeight - collapsedHeight) >= 1;
        this.showMoreExpanded = false;
        this.updateShowMoreButton(false);

        this.animateShowMoreHeight(startHeight, collapsedHeight, animate, () => {
          this.elements.viewerWrap.style.height = `${collapsedHeight}px`;
          this.classList.remove('product__media-gallery--show-more-collapsing');
          this.classList.add('product__media-gallery--show-more-collapsed');

          if (animate) {
            this.alignCollapsedShowMoreButton();
          }
        });

        if (shouldTrackCollapseScroll) {
          this.trackCollapsedShowMoreButton(startHeight, collapsedHeight);
        }
      }

      resetShowMore(items = this.getShowMoreItems()) {
        this.stopShowMoreAnimation();
        this.showMoreExpanded = false;
        this.elements?.viewerWrap?.style.removeProperty('height');
        this.classList.remove(
          'product__media-gallery--show-more-available',
          'product__media-gallery--show-more-expanded',
          'product__media-gallery--show-more-expanding',
          'product__media-gallery--show-more-collapsed',
          'product__media-gallery--show-more-collapsing'
        );
        this.setCollapsedPairLayout(items, false);
        this.setCollapsedMediaAccessibility(items, false);
        this.updateShowMoreButton(false);
      }

      updateShowMoreButton(expanded) {
        if (!this.showMoreButton) return;

        this.showMoreButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (this.showMoreLabel) {
          this.showMoreLabel.textContent = expanded ? this.showLessText : this.showMoreText;
        }
      }

      setCollapsedMediaAccessibility(items, collapsed) {
        items.forEach((item, index) => {
          const isConcealed = collapsed && index >= this.showMoreLimit;
          item.toggleAttribute('inert', isConcealed);

          if (isConcealed) {
            item.setAttribute('aria-hidden', 'true');
          } else {
            item.removeAttribute('aria-hidden');
          }
        });
      }

      setCollapsedPairLayout(items, collapsed) {
        items.forEach((item) => item.classList.remove('product__media-item--show-more-pair'));

        if (!collapsed || this.showMoreLimit < 2 || this.showMoreLimit % 3 !== 2) return;

        items[this.showMoreLimit - 2]?.classList.add('product__media-item--show-more-pair');
        items[this.showMoreLimit - 1]?.classList.add('product__media-item--show-more-pair');
      }

      getCollapsedShowMoreHeight(items) {
        const lastVisibleItem = items[Math.min(this.showMoreLimit, items.length) - 1];
        if (!lastVisibleItem || !this.elements?.viewerWrap) return 0;

        const viewerWrapBounds = this.elements.viewerWrap.getBoundingClientRect();
        const itemBounds = lastVisibleItem.getBoundingClientRect();
        return Math.max(0, Math.ceil(itemBounds.bottom - viewerWrapBounds.top));
      }

      setCollapsedShowMoreHeight(items) {
        if (!items.length || !this.elements?.viewerWrap || !this.elements?.viewer) return;

        this.setCollapsedPairLayout(items, true);
        const fullHeight = Math.ceil(this.elements.viewer.getBoundingClientRect().height);
        const collapsedHeight = Math.min(this.getCollapsedShowMoreHeight(items), fullHeight);
        this.elements.viewerWrap.style.height = `${collapsedHeight}px`;
      }

      animateShowMoreHeight(startHeight, endHeight, animate, onComplete) {
        const animationTarget = this.elements?.viewerWrap;
        if (!animationTarget || !Number.isFinite(startHeight) || !Number.isFinite(endHeight)) return;

        if (!animate || this.prefersReducedMotion() || Math.abs(startHeight - endHeight) < 1) {
          animationTarget.style.height = `${endHeight}px`;
          onComplete();
          return;
        }

        this.classList.add('product__media-gallery--show-more-animating');
        animationTarget.style.height = `${startHeight}px`;
        animationTarget.getBoundingClientRect();

        const finish = (event) => {
          if (event && (event.target !== animationTarget || event.propertyName !== 'height')) return;

          this.stopShowMoreAnimation();
          onComplete();
        };

        this.showMoreTransitionHandler = finish;
        animationTarget.addEventListener('transitionend', finish);
        this.showMoreAnimationTimer = window.setTimeout(finish, 650);
        this.showMoreAnimationFrame = window.requestAnimationFrame(() => {
          animationTarget.style.height = `${endHeight}px`;
        });
      }

      trackCollapsedShowMoreButton(startHeight, endHeight) {
        if (!this.showMoreButton || !this.elements?.viewerWrap || !this.isShowMoreDesktop()) return;

        const collapseDistance = startHeight - endHeight;
        if (collapseDistance <= 0) return;

        const startButtonBottom = this.showMoreButton.getBoundingClientRect().bottom;

        if (this.showMoreScrollFrame) {
          window.cancelAnimationFrame(this.showMoreScrollFrame);
        }

        const trackButton = () => {
          this.showMoreScrollFrame = null;

          if (
            !this.classList.contains('product__media-gallery--show-more-animating') ||
            !this.isShowMoreDesktop()
          ) {
            return;
          }

          const currentHeight = this.elements.viewerWrap.getBoundingClientRect().height;
          const collapseProgress = Math.min(
            1,
            Math.max(0, (startHeight - currentHeight) / collapseDistance)
          );
          const targetButtonBottom = this.getCollapsedShowMoreButtonBottom();
          const desiredButtonBottom =
            startButtonBottom + (targetButtonBottom - startButtonBottom) * collapseProgress;

          this.alignShowMoreButtonBottom(desiredButtonBottom);

          if (collapseProgress < 1) {
            this.showMoreScrollFrame = window.requestAnimationFrame(trackButton);
          }
        };

        this.showMoreScrollFrame = window.requestAnimationFrame(trackButton);
      }

      alignCollapsedShowMoreButton() {
        if (!this.showMoreButton || !this.isShowMoreDesktop()) return;

        this.alignShowMoreButtonBottom(this.getCollapsedShowMoreButtonBottom());
      }

      getCollapsedShowMoreButtonBottom() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        return Math.max(0, viewportHeight - 24);
      }

      alignShowMoreButtonBottom(desiredButtonBottom) {
        const buttonBounds = this.showMoreButton.getBoundingClientRect();
        const scrollAdjustment = buttonBounds.bottom - desiredButtonBottom;
        if (Math.abs(scrollAdjustment) < 0.5) return;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.body?.scrollHeight || 0
        );
        const maxScrollTop = Math.max(0, documentHeight - viewportHeight);
        const targetScrollTop = Math.min(
          maxScrollTop,
          Math.max(0, currentScrollTop + scrollAdjustment)
        );

        window.scrollTo({
          top: targetScrollTop,
          behavior: 'auto',
        });
      }

      stopShowMoreAnimation() {
        if (this.showMoreAnimationFrame) {
          window.cancelAnimationFrame(this.showMoreAnimationFrame);
          this.showMoreAnimationFrame = null;
        }

        if (this.showMoreScrollFrame) {
          window.cancelAnimationFrame(this.showMoreScrollFrame);
          this.showMoreScrollFrame = null;
        }

        if (this.showMoreAnimationTimer) {
          window.clearTimeout(this.showMoreAnimationTimer);
          this.showMoreAnimationTimer = null;
        }

        if (this.showMoreTransitionHandler && this.elements?.viewerWrap) {
          this.elements.viewerWrap.removeEventListener('transitionend', this.showMoreTransitionHandler);
          this.showMoreTransitionHandler = null;
        }

        this.classList.remove('product__media-gallery--show-more-animating');
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }

        this.updateShowMoreState({ animate: false });
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items=[]) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4]
        });

        // this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
        //   if (this.sliderInstance && this.sliderInstance.slider) {
        //     const { slides, activeIndex } = this.sliderInstance.slider;
        //     if (slides[activeIndex]) {
        //       const el = slides[activeIndex].querySelector('img');
        //       if (el) {
        //         return el;
        //       }
        //     }
        //   }

        //   return thumbEl;
        // });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          // if (this.sliderInstance && this.sliderInstance.slider) {
          //   const { slides, activeIndex } = this.sliderInstance.slider;
          //   if (slides[activeIndex]) {
          //     const el = slides[activeIndex].querySelector('img');
          //     if (el) {
          //       return el.src;
          //     }
          //   }
          // }
          if (data.src) {
            return data.src;
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        this.initImageZoom(newMedias);
        this.updateShowMoreState({ animate: false });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
